'use client'
import { LinePlayCircleVideo } from '@qr-front/icons/line-play-circle-video'
import { theme } from '@qr-front/ui'
import { cn, getVersionForClone } from '@qr-front/ui/utils'
import { VideoPlayer } from 'features/generator/contentFormSections/VideoForm/VideoSection/VideoPlayer'
import { useAtomValue } from 'jotai'
import NextImage from 'next/image'
import { useTranslations } from 'next-intl'
import { useRef } from 'react'

import { primaryColorAtom } from '@/content-from-sections/client'

import { videoListAtom } from '../../contentFormSections/VideoForm/VideoSection/VideoDetailsFields'
import { QrScreen, TQrScreenProps } from '../../QrScreen/QrScreen'
import { videoDemoContent } from './videoDemoContent'
import { IsPreviewModeProvider } from './VideoScreenContexts'
import { VideosScreenHeader } from './VideosScreenHeader'

export const VIDEO_DEMO_PRIMARY_COLOR = getVersionForClone({
	base: theme.colors['red-50'],
	clone1: '#6FC5C4',
})

export const Content = ({ previewMode, demoMode }: TQrScreenProps) => {
	const t = useTranslations()

	const videoList = useAtomValue(videoListAtom)

	const demoContent = videoDemoContent(t)

	const contentRef = useRef<HTMLDivElement>(null)

	const hasScroll =
		contentRef.current &&
		contentRef.current.scrollHeight > contentRef.current.clientHeight

	return (
		<IsPreviewModeProvider value={previewMode}>
			<div
				className={cn('relative', demoMode && 'pointer-events-none')}
				ref={contentRef}
			>
				<div className={cn('flex flex-col gap-6 pb-6 transition-colors')}>
					<VideosScreenHeader previewMode={previewMode} demoMode={demoMode} />

					<div
						className={cn(
							'flex flex-col items-center gap-2 px-5',
							hasScroll && 'pb-8',
						)}
					>
						{(demoMode ? demoContent.videos : videoList).map((item, index) => {
							const hasNameOrDescription =
								item.video.name || item.video.description

							return (
								<div
									key={index}
									className="flex h-fit flex-col rounded-4 bg-white shadow-generator"
								>
									<div
										className={cn(
											'relative h-[132px] w-[220px] p-0.5',
											hasNameOrDescription && 'pb-0',
										)}
									>
										{item.video.source.demo ? (
											<>
												<div className="absolute inset-0 z-10 flex items-center justify-center">
													<LinePlayCircleVideo
														className="shrink-0"
														color="white"
													/>
												</div>
												<NextImage
													src={item.video.source.demo}
													width={220}
													height={132}
													alt={item.video.name}
													className="h-full w-full rounded-4 object-cover"
												/>
											</>
										) : (
											<VideoPlayer item={item} previewMode={previewMode} />
										)}
									</div>
									{hasNameOrDescription && (
										<div className="max-w-[220px] p-2 word-break-fix">
											<div className="type-body-small font-semibold">
												{item.video.name}
											</div>

											<div className="type-body-caption font-normal">
												{item.video.description}
											</div>
										</div>
									)}
								</div>
							)
						})}
					</div>
				</div>
			</div>
		</IsPreviewModeProvider>
	)
}

export const VideoScreen = (props: TQrScreenProps) => {
	const primaryColor = useAtomValue(primaryColorAtom)

	return (
		<QrScreen
			{...props}
			primaryColor={props.demoMode ? VIDEO_DEMO_PRIMARY_COLOR : primaryColor}
			content={<Content {...props} />}
		/>
	)
}
